import {
  CalendarDays,
  CheckCheck,
  Clock,
  Megaphone,
  Pencil,
  User as UserIcon,
  X,
} from 'lucide-react';
import type { Task } from '@underline/shared';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/users/user-avatar';
import { useUsers } from '@/hooks/use-users';
import { useEvents } from '@/hooks/use-events';
import { useAuthStore } from '@/stores/auth-store';
import { fmtDateBR, fmtTime, isOverdue } from '@/lib/dates';

interface Props {
  open: boolean;
  task: Task | null;
  onClose: () => void;
  onEdit: () => void;
}

export function TaskViewModal({ open, task, onClose, onEdit }: Props) {
  const me = useAuthStore((s) => s.user);
  const { data: users = [] } = useUsers();
  const { data: events = [] } = useEvents();
  if (!task) return null;

  const canEdit = !!me && (me.role === 'admin' || me.id === task.created_by);
  const responsible = users.find((u) => u.id === task.responsible_id);
  const creator = users.find((u) => u.id === task.created_by);
  const event = task.event_id ? events.find((e) => e.id === task.event_id) : null;
  const done = task.status === 'done';
  const overdue = !done && isOverdue(task.due_date, task.due_time);

  return (
    // Sem header proprio do sheet: o prototipo desenha o badge + X no corpo
    <BottomSheet open={open} onClose={onClose} hideClose>
      {/* Topo: marcador "post" + badge de status + botao fechar redondo */}
      <div className="flex items-center justify-between mb-3.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <Megaphone className="h-3 w-3" /> post
          </span>
          <Badge variant={done ? 'feito' : overdue ? 'atrasado' : 'pendente'}>
            {done ? 'feito' : overdue ? 'atrasado' : 'pendente'}
          </Badge>
        </div>
        <button
          onClick={onClose}
          aria-label="Fechar"
          className="grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-muted-foreground transition active:scale-[0.97] hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Titulo do post (display 24px) */}
      <h2 className="u-event-title text-[24px] text-foreground mb-[18px]">{task.title}</h2>

      {/* Grid de detalhes: Data + Horario */}
      <div className="grid grid-cols-2 gap-2.5 mb-[22px]">
        <DetailBox
          icon={<CalendarDays className="h-[11px] w-[11px]" />}
          label="Data"
          value={fmtDateBR(task.due_date)}
        />
        <DetailBox
          icon={<Clock className="h-[11px] w-[11px]" />}
          label="Horário"
          value={task.due_time ? fmtTime(task.due_time) : '—'}
        />
      </div>

      {/* Responsavel */}
      {responsible && (
        <div className="mb-5">
          <p className="eyebrow mb-2.5">responsável</p>
          <div className="flex items-center gap-3 rounded-lg border border-border px-3.5 py-3">
            <UserAvatar
              name={responsible.name}
              colorHex={responsible.color_hex}
              url={responsible.avatar_url}
              size="sm"
            />
            <span className="text-sm font-semibold text-foreground">{responsible.name}</span>
          </div>
        </div>
      )}

      {/* Evento vinculado */}
      {event && (
        <div className="mb-5">
          <p className="eyebrow mb-1.5">evento vinculado</p>
          <p className="text-sm text-foreground">{event.name}</p>
        </div>
      )}

      {/* Briefing */}
      {task.description && (
        <div className="mb-5">
          <p className="eyebrow mb-1.5">briefing</p>
          <p className="text-sm whitespace-pre-wrap leading-relaxed text-foreground">
            {task.description}
          </p>
        </div>
      )}

      {done && task.done_at && (
        <p className="flex items-center gap-1 text-[11px] text-success">
          <CheckCheck className="h-3 w-3" /> Concluído em {fmtDateBR(task.done_at.slice(0, 10))}
        </p>
      )}

      {creator && (
        <p className="flex items-center gap-1 text-[11px] text-muted-foreground mt-2">
          <UserIcon className="h-3 w-3" /> Criado por {creator.name}
        </p>
      )}

      {/* Rodape: Fechar (secondary) + Editar (primary) */}
      {canEdit ? (
        <div className="flex gap-2.5 mt-[22px]">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Fechar
          </Button>
          <Button onClick={onEdit} className="flex-1">
            <Pencil className="h-[15px] w-[15px]" /> Editar
          </Button>
        </div>
      ) : (
        <p className="text-center text-xs text-muted-foreground mt-[22px]">
          Somente o criador ou admins podem editar este post.
        </p>
      )}
    </BottomSheet>
  );
}

// Caixa de detalhe: eyebrow com icone + valor em mono
function DetailBox({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg bg-surface-2 px-3.5 py-3">
      <p className="eyebrow text-[9px] inline-flex items-center gap-1.5">
        {icon}
        {label}
      </p>
      <p className="mt-1 font-mono text-[13px] font-semibold text-foreground">{value}</p>
    </div>
  );
}
